import { useCallback } from 'react';

import { useEditCanvasStore } from '../store';
import { type AppNode } from '../types';
import { type PasteGroup } from './clipboard/utils';

export type CloneResult = {
  clones: AppNode[];
  groups: PasteGroup[];
};

export function useCloneCanvasNodes() {
  const canvasNodes = useEditCanvasStore((state) => state.nodes);

  return useCallback(
    (nodes: { id: string }[]): CloneResult => {
      const nodeIds = new Set(nodes.map((n) => n.id));
      const groupIdToIndices = new Map<string, number[]>();

      const clones = canvasNodes
        .filter((obj) => nodeIds.has(obj.id))
        .map((obj, index) => {
          if (obj.groupId) {
            const existing = groupIdToIndices.get(obj.groupId);

            if (existing) {
              existing.push(index);
            } else {
              groupIdToIndices.set(obj.groupId, [index]);
            }
          }

          const cloned = structuredClone(obj);
          cloned.id = crypto.randomUUID();
          cloned.remoteId = undefined;
          cloned.groupId = undefined;
          cloned.selected = false;

          if ('task' in cloned.data) {
            cloned.data.task = undefined;
          }

          if (cloned.type === 'image') {
            cloned.data.isProcessing = false;
          }

          if (cloned.type === 'text') {
            cloned.data.isEditing = false;
          }

          return cloned;
        });

      const groups: PasteGroup[] = [];

      for (const [, indices] of groupIdToIndices) {
        if (indices.length >= 2) {
          groups.push({ memberIndices: indices });
        }
      }

      return { clones, groups };
    },
    [canvasNodes],
  );
}
